import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Share2, Loader2, AlertTriangle } from "lucide-react";
import { nodeToPng, shareOrDownload } from "@/lib/share";
import { bumpStreak } from "@/lib/device";
import { haptic } from "@/lib/haptics";
import { toast } from "sonner";

const ACCUSE_LINES = [
  "This didn't eat itself.",
  "A fridge remembers. So do I.",
  "No names. But we both know.",
  "Replacements are, frankly, expected.",
  "I'm not angry. I'm just documenting.",
];

const AccusationCard = forwardRef(({ item, culprit, line }, ref) => (
  <div
    ref={ref}
    style={{
      width: 1080,
      height: 1080,
      background: "#1a1a1a",
      color: "#f4f1ea",
      padding: "90px",
      display: "flex",
      flexDirection: "column",
      boxSizing: "border-box",
      fontFamily: "'IBM Plex Sans', sans-serif",
    }}
  >
    <div style={{ fontSize: 30, letterSpacing: 8, textTransform: "uppercase", fontWeight: 600, color: "#d4a373" }}>
      A friendly accusation
    </div>

    <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
      <div style={{ fontSize: 42, color: "#a8a49b" }}>Who ate the</div>
      <div
        style={{
          fontFamily: "'Playfair Display', serif",
          fontWeight: 900,
          fontSize: 150,
          lineHeight: 0.92,
          letterSpacing: -4,
          margin: "16px 0 40px",
        }}
      >
        {item || "leftovers"}?
      </div>
      <div style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontSize: 52, lineHeight: 1.25, color: "#f4f1ea" }}>
        “{line}”
      </div>
      {culprit ? (
        <div style={{ fontSize: 38, marginTop: 44, color: "#d4a373" }}>
          Prime suspect: <span style={{ fontWeight: 700 }}>{culprit}</span>
        </div>
      ) : null}
    </div>

    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
      <div style={{ width: 34, height: 34, borderRadius: 999, border: "3px solid #f4f1ea" }} />
      <div style={{ fontSize: 30, fontWeight: 600, letterSpacing: 1 }}>What's in the Fridge</div>
    </div>
  </div>
));
AccusationCard.displayName = "AccusationCard";

export default function AccusationModal({ result, onClose }) {
  const goneItems = useMemo(
    () => (result.items || []).filter((i) => i.status === "gone").map((i) => i.name),
    [result]
  );
  const [step, setStep] = useState("pick");
  const [item, setItem] = useState(goneItems[0] || "");
  const [culprit, setCulprit] = useState("");
  const [busy, setBusy] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const line = useMemo(() => ACCUSE_LINES[Math.floor(Math.random() * ACCUSE_LINES.length)], [step]);
  const cardRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleShare = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await nodeToPng(cardRef.current);
      await shareOrDownload(dataUrl, "fridge-accusation.png", `Who ate the ${item}?`);
      const n = bumpStreak();
      haptic([12, 40, 12]);
      toast(`Accusation sent. ${n} card${n > 1 ? "s" : ""} shared.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="accusation-modal"
    >
      <motion.div
        className="w-full max-w-md bg-[#eef5f6]/95 backdrop-blur rounded-t-2xl sm:rounded-lg p-6 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] max-h-[92dvh] overflow-y-auto"
        initial={{ y: 40 }}
        animate={{ y: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif text-2xl">Accusation mode</h3>
          <button data-testid="accuse-close-btn" onClick={onClose} className="w-9 h-9 rounded-full glass flex items-center justify-center">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <AnimatePresence mode="wait">
          {step === "pick" && (
            <motion.div key="pick" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <p className="text-sm text-[#5c5c5c] mb-4">Name the missing item. Keep it light — this is comedy, not evidence.</p>

              {goneItems.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {goneItems.map((g) => (
                    <button
                      key={g}
                      data-testid={`accuse-item-${g}`}
                      onClick={() => setItem(g)}
                      className={`px-3 py-2 rounded-sm text-sm transition-colors ${
                        item === g ? "bg-[#1a1a1a] text-[#f4f1ea]" : "glass text-[#1a1a1a]"
                      }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              )}

              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c5c5c]">The missing item</label>
              <input
                data-testid="accuse-item-input"
                value={item}
                onChange={(e) => setItem(e.target.value)}
                placeholder="e.g. the last yoghurt"
                className="w-full glass rounded-sm px-4 py-3 mt-2 mb-4 outline-none focus:ring-2 focus:ring-[#1a1a1a]"
              />

              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c5c5c]">Prime suspect (optional)</label>
              <input
                data-testid="accuse-culprit-input"
                value={culprit}
                onChange={(e) => setCulprit(e.target.value)}
                placeholder="e.g. Dave"
                className="w-full glass rounded-sm px-4 py-3 mt-2 mb-6 outline-none focus:ring-2 focus:ring-[#1a1a1a]"
              />

              <button
                data-testid="accuse-next-btn"
                disabled={!item.trim()}
                onClick={() => setStep("confirm")}
                className="btn-ink w-full rounded-sm py-4 flex items-center justify-center gap-2 disabled:opacity-40"
              >
                Continue <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </motion.div>
          )}

          {step === "confirm" && (
            <motion.div key="confirm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center py-4">
              <div className="w-12 h-12 rounded-full glass mx-auto flex items-center justify-center mb-5">
                <AlertTriangle className="w-6 h-6 text-[#c65d47]" strokeWidth={1.5} />
              </div>
              <p className="font-serif text-3xl leading-tight">Are you sure you want to start a war?</p>
              <p className="text-sm text-[#5c5c5c] mt-3 mb-7">
                It's a joke card, not a court summons. Send it with love.
              </p>
              <div className="flex gap-3">
                <button
                  data-testid="accuse-back-btn"
                  onClick={() => setStep("pick")}
                  className="flex-1 glass rounded-sm py-4"
                >
                  Stand down
                </button>
                <button
                  data-testid="accuse-confirm-btn"
                  onClick={() => setStep("card")}
                  className="flex-1 btn-ink rounded-sm py-4"
                >
                  Send the friendly accusation
                </button>
              </div>
            </motion.div>
          )}

          {step === "card" && (
            <motion.div key="card" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="flex justify-center">
                <div style={{ width: 300, height: 300 }} className="overflow-hidden rounded-sm hairline shadow-[0_8px_32px_rgba(0,0,0,0.12)]">
                  <div style={{ transform: "scale(0.2778)", transformOrigin: "top left" }}>
                    <AccusationCard ref={cardRef} item={item} culprit={culprit} line={line} />
                  </div>
                </div>
              </div>
              <button
                data-testid="accuse-share-btn"
                onClick={handleShare}
                disabled={busy}
                className="btn-ink w-full rounded-sm py-4 mt-6 flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-5 h-5 spin-slow" /> : <Share2 className="w-5 h-5" strokeWidth={1.5} />}
                {busy ? "Developing…" : "Send it"}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}
